
import { X, Calendar, Heading as Bread, Wine, FileText } from 'lucide-react';
import { SainteCeneReport } from '../../types/sainteCeneReport';
import { format } from '../../utils/format';

interface SainteCeneReportPreviewProps {
  report: SainteCeneReport;
  onClose: () => void;
}

export default function SainteCeneReportPreview({ report, onClose }: SainteCeneReportPreviewProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString + 'T00:00:00');
    return format.date(date);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center">
              <FileText className="mr-2 text-[#00665C]" />
              Rapport Sainte Cène
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Date */}
          <div className="mb-8">
            <div className="flex items-center text-lg font-semibold text-gray-800 mb-2">
              <Calendar className="w-5 h-5 mr-2 text-[#00665C]" />
              Date du Rapport
            </div>
            <p className="text-2xl font-bold text-[#00665C]">
              {formatDate(report.date)}
            </p>
          </div>

          {/* Rapport Content */}
          <div className="space-y-8">
            {/* Titre du rapport */}
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-gray-800">
                Point de la Sainte Cène du {formatDate(report.date)}
              </h3>
            </div>

            {/* Quantité Préparée */}
            <div className="bg-blue-50 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-blue-800 mb-4 flex items-center">
                <Wine className="w-5 h-5 mr-2" />
                Quantité de Sainte Cène Préparée :
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-center mb-2">
                    <Bread className="w-4 h-4 mr-2 text-amber-600" />
                    <span className="font-medium text-gray-700">Pains</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-600">
                    {report.painsPreparees.toString().padStart(2, '0')}
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-center mb-2">
                    <Wine className="w-4 h-4 mr-2 text-[#00665C]" />
                    <span className="font-medium text-gray-700">Vins</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-600">
                    {report.vinsPreparees.toString().padStart(2, '0')}
                  </div>
                </div>
              </div>
            </div>

            {/* Quantité Distribuée */}
            <div className="bg-green-50 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-green-800 mb-4">
                Quantité Distribuée :
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-center mb-2">
                    <Bread className="w-4 h-4 mr-2 text-amber-600" />
                    <span className="font-medium text-gray-700">Pains</span>
                  </div>
                  <div className="text-2xl font-bold text-green-600">
                    {report.painsDistribuees.toString().padStart(2, '0')}
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-center mb-2">
                    <Wine className="w-4 h-4 mr-2 text-[#00665C]" />
                    <span className="font-medium text-gray-700">Vins</span>
                  </div>
                  <div className="text-2xl font-bold text-green-600">
                    {report.vinsDistribuees.toString().padStart(2, '0')}
                  </div>
                </div>
              </div>
            </div>

            {/* Quantité Restante */}
            <div className="bg-orange-50 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-orange-800 mb-4">
                Quantité Restantes :
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-center mb-2">
                    <Bread className="w-4 h-4 mr-2 text-amber-600" />
                    <span className="font-medium text-gray-700">Pains</span>
                  </div>
                  <div className="text-2xl font-bold text-orange-600">
                    {report.painsRestantes.toString().padStart(2, '0')}
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-center mb-2">
                    <Wine className="w-4 h-4 mr-2 text-[#00665C]" />
                    <span className="font-medium text-gray-700">Vins</span>
                  </div>
                  <div className="text-2xl font-bold text-orange-600">
                    {report.vinsRestantes.toString().padStart(2, '0')}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}